import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  buildVars,
  varsToCssText,
  DEFAULT_SKIN,
  SKINS,
  SKIN_IDS,
} from "./tokens";

// Appearance preferences are localStorage-backed so they apply instantly
// (before login, and before any cloud pull). App.js mirrors them into the
// synced settings row so they follow the account across devices.
const KEYS = { theme: "xz-theme", skin: "xz-skin", ambient: "xz-ambient" };
const THEMES = ["system", "light", "dark"];

const read = (k, d) => {
  try {
    const v = localStorage.getItem(k);
    return v === null ? d : v;
  } catch (e) {
    return d;
  }
};
const write = (k, v) => {
  try {
    localStorage.setItem(k, v);
  } catch (e) {
    /* ignore quota / private mode */
  }
};

export const normalizeTheme = (t) => (THEMES.includes(t) ? t : "system");
export const normalizeSkin = (s) => (SKIN_IDS.includes(s) ? s : DEFAULT_SKIN);

const prefersDark = () =>
  typeof window !== "undefined" && window.matchMedia
    ? window.matchMedia("(prefers-color-scheme: dark)").matches
    : true;

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() =>
    normalizeTheme(read(KEYS.theme, "system")),
  );
  const [skin, setSkinState] = useState(() =>
    normalizeSkin(read(KEYS.skin, DEFAULT_SKIN)),
  );
  const [ambientEffects, setAmbientState] = useState(
    () => read(KEYS.ambient, "1") !== "0",
  );
  const [systemDark, setSystemDark] = useState(prefersDark);

  // Track the OS preference so "system" follows it live.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e) => setSystemDark(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  const resolvedTheme =
    theme === "system" ? (systemDark ? "dark" : "light") : theme;

  // Apply the token set: one <style> element with every variable, plus
  // attributes/classes CSS can hook into and the PWA theme-color.
  useEffect(() => {
    const vars = buildVars(skin, resolvedTheme);
    let el = document.getElementById("xz-theme-vars");
    if (!el) {
      el = document.createElement("style");
      el.id = "xz-theme-vars";
      document.head.appendChild(el);
    }
    el.textContent = varsToCssText(vars);
    const html = document.documentElement;
    html.dataset.theme = resolvedTheme;
    html.dataset.skin = skin;
    html.classList.toggle("dark", resolvedTheme === "dark");
    html.style.colorScheme = resolvedTheme;
    html.style.backgroundColor = ""; // boot-script fallback no longer needed
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", vars["--theme-color"]);
  }, [skin, resolvedTheme]);

  const setTheme = useCallback((t) => {
    const v = normalizeTheme(t);
    setThemeState(v);
    write(KEYS.theme, v);
  }, []);
  const setSkin = useCallback((s) => {
    const v = normalizeSkin(s);
    setSkinState(v);
    write(KEYS.skin, v);
  }, []);
  const setAmbientEffects = useCallback((b) => {
    const v = !!b;
    setAmbientState(v);
    write(KEYS.ambient, v ? "1" : "0");
  }, []);
  const cycleTheme = useCallback(() => {
    setThemeState((prev) => {
      const next =
        prev === "system" ? "light" : prev === "light" ? "dark" : "system";
      write(KEYS.theme, next);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      theme,
      resolvedTheme,
      skin,
      ambientEffects,
      skins: SKINS,
      setTheme,
      setSkin,
      setAmbientEffects,
      cycleTheme,
    }),
    [
      theme,
      resolvedTheme,
      skin,
      ambientEffects,
      setTheme,
      setSkin,
      setAmbientEffects,
      cycleTheme,
    ],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
